import classNames from 'classnames';
import * as _ from 'lodash';
import { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type { Column, RowRenderProps, SortingRule } from 'react-table';

import { Action, ResourceType } from '../../../../../typings/permissions';
import { useDialogContext } from '../../../../contexts/DialogContext';
import { window } from '../../../../globals';
import { roleLabel } from '../../../../helpers/roles';
import useMaskAsUser from '../../../../hooks/api/masquerade/useMaskAsUser';
import useRoles from '../../../../hooks/api/roles/useRoles';
import TeamsModule from '../../../../redux/teams';
import UsersModule from '../../../../redux/users';
import { splitArray } from '../../../../utils';
import { icons } from '../../../generic/Icon';
import Loader from '../../../generic/Loader';
import Pill from '../../../generic/Pill/Pill';
import SimpleReactTable from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import Tooltip from '../../../generic/Tooltip';
import SettingsTableRowActions from '../../shared/SettingsTableRowActions';
import type { Role } from '../../../../../shared/model-typings';
import type { ReduxStore } from '../../../../redux/store';
import type { UsersStore } from '../../../../redux/users';
import type { Team } from '../../../../types';

import { NONE_ROLE } from './constants';

import styles from './UsersTable.module.less';

const ACCOUNT_DISABLED_TOOLTIP = 'This user account is disabled.';

const filterPassthrough = _.constant(true);

const disabledUserClassname = ({ enabled }: UserRow) => enabled ? undefined : 'disabled-user';

type UserEditActionsProps = {
  userId: number;
  canEditUsers: boolean;
  canDeleteUser: boolean;
  confirmDeleteUser(): void;
};

/**
 * This is set of actions for viewing, editing, and deleting a user.
 */
const UserEditActions = ({
  userId,
  canEditUsers,
  canDeleteUser,
  confirmDeleteUser,
}: UserEditActionsProps) => {
  const { mutate: maskAsUser } = useMaskAsUser();
  return (
    <SettingsTableRowActions>
      {window.user?.super && (
        <SettingsTableRowActions.IconButton
          icon={icons.mask}
          onClick={async () => {
            maskAsUser(userId);
          }}
          tooltip="Masquerade as User"
          tooltipId={`masquerade-user-${userId}`}
        />
      )}
      <SettingsTableRowActions.ViewOrEditLink
        to={`/account/settings/organization/users/${userId}`}
        tooltip={canEditUsers ? 'View/Edit User' : 'You do not have permission to view or edit this user.'}
        tooltipId={`view-edit-user-${userId}`}
        canEdit={canEditUsers}
        isDisabled={!canEditUsers}
      />
      <SettingsTableRowActions.IconButton
        icon={icons.trashRegular}
        onClick={confirmDeleteUser}
        tooltipId={`delete-user-${userId}`}
        tooltip="Remove User"
        isDisabled={!canDeleteUser}
      />
    </SettingsTableRowActions>
  );
};

type ColumnRenderParameter = { original: UserRow } & Omit<RowRenderProps, 'original'>;

type UserRow = {
  id: number;
  name: string;
  email: string;
  enabled: boolean;
  isServiceAccount: boolean;
  roleName: string;
  teams: Array<{ name: string; roleName: string }>;
};

type ReduxStateProps = {
  teams: Team[];
};

type DispatchProps = {
  deleteUser(userId: number): void;
};

type Props = ReduxStateProps & DispatchProps & {
  users: UsersStore['users'];
  searchQuery: string;
  orgRoleFilter: Role | 'None' | null;
};

/**
 * This is the table of users for the organization settings page.
 */
const UsersTable = ({
  users,
  teams,
  searchQuery,
  orgRoleFilter,
  deleteUser,
}: Props) => {
  const { onConfirm } = useDialogContext();
  const { data: rolesData = [] } = useRoles();
  const roles = _.keyBy(rolesData, (role) => role.id);

  const [sortMode, setSortMode] = useState<SortingRule>({ id: 'name', desc: false });

  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);

  const allUserRows = useMemo<UserRow[]>(
    () => Object.values(users)
      .map((user): UserRow => ({
        id: user.id,
        name: user.full_name || user.username || '(None)',
        email: user.email || '(None)',
        enabled: user.enabled,
        isServiceAccount: user.isServiceAccount,
        roleName: roleLabel(roles, user.userRole?.roleId || null),
        teams: teams.reduce((acc, team) => {
          const teamUser = team.teamUsers?.find(({ userId }) => userId === user.id);
          if (teamUser) {
            return [...acc, {
              name: team.name,
              roleName: roleLabel(roles, teamUser.roleId),
            }];
          }
          return acc;
        }, [] as Array<{ name: string; roleName: string }>),
      })),
    [users, teams, roles],
  );

  const canDeleteUser = window.hasPermission({ action: Action.Remove, resourceType: ResourceType.UserAny });

  const canEditUsers = canDeleteUser ||
    window.hasPermission({ action: Action.Manage, resourceType: ResourceType.Role });

  const confirmDeleteUser = (userId: number) => {
    if (canDeleteUser) {
      onConfirm({
        title: 'Remove User?',
        body: 'Are you sure you want to permanently remove this user? This action cannot be undone.',
        onConfirm: () => {
          setDeletingUserId(userId);
          deleteUser(userId);
        },
        confirmBtnType: 'error',
        confirmText: 'Remove',
      });
    }
  };

  const filteredUsers = (): UserRow[] => {
    //  Early return if there is no search/role filter.
    if (!searchQuery && !orgRoleFilter) {
      return allUserRows;
    }

    //  Filter based on search query.
    const searchLower = searchQuery.toLowerCase();
    const searchFilter = !searchQuery ? filterPassthrough : (user: UserRow) =>
      user.name.toLowerCase().includes(searchLower) || user.email.toLowerCase().includes(searchLower);
    //  Filter based on role filter.
    const roleFilter = !orgRoleFilter
      ? filterPassthrough
      : (user: UserRow) => (
          orgRoleFilter === NONE_ROLE ? user.roleName === NONE_ROLE : user.roleName === orgRoleFilter.name
        );
    //  Combine all filters, so each userRow can be tested in a single Array.filter pass.
    const allFilters = (user: UserRow) => [searchFilter, roleFilter].every(filterFn => filterFn(user));
    return allUserRows.filter(allFilters);
  };

  const getColumns = (): Array<Column<UserRow>> => {
    const cols: Column[] = [{
      id: 'name',
      Header: (
        <TableHeader title="User" sort={{ id: 'name', currentMode: sortMode }} />
      ),
      accessor: 'name',
      width: 300,
      resizable: false,
      style: {
        textOverflow: 'ellipsis',
        display: 'block',
      },
      // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now
      Cell: ({ original }: ColumnRenderParameter) => (
        <Tooltip id={`user-tooltip-${original.id}`} tooltip={ACCOUNT_DISABLED_TOOLTIP} showTooltip={!original.enabled}>
          <div className={styles.userColumn}>
            <div className={styles.info}>
              <span className={classNames(styles.name, disabledUserClassname(original))}>{original.name}</span>
              <span className={disabledUserClassname(original)}>{original.email}</span>
            </div>
            {original.isServiceAccount && <Pill color="blue">Service Account</Pill>}
          </div>
        </Tooltip>
      ),
    }];

    if (window.hasPermission({ resourceType: ResourceType.Role, action: Action.Manage })) {
      cols.push({
        id: 'roleName',
        Header: (
          <TableHeader title="Organization Role" sort={{ id: 'roleName', currentMode: sortMode }} />
        ),
        accessor: 'roleName',
        width: 200,
        resizable: false,
        // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now
        Cell: ({ original }: ColumnRenderParameter) => (
          <Tooltip id={`role-tooltip-${original.id}`} tooltip={ACCOUNT_DISABLED_TOOLTIP} showTooltip={!original.enabled}>
            <span className={classNames(disabledUserClassname(original), 'inline-block')} style={{ whiteSpace: 'normal' }}>
              {original.roleName}
            </span>
          </Tooltip>
        ),
      }, {
        id: 'teams',
        Header: (
          <TableHeader title="Teams" />
        ),
        accessor: 'teams',
        resizable: false,
        sortable: false,
        // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now
        Cell: ({ original }: ColumnRenderParameter) => {
          const teamRoles = original.teams.map(({ name, roleName }) => `${name} (${roleName})`);
          const teamsCount = original.teams.length;
          const listShownCount = 1;
          const tooltipShownCount = 50;
          const [first, tooltipItems] = splitArray(teamRoles, listShownCount);
          const tooltipMoreCount = teamsCount - listShownCount - tooltipShownCount;

          return (
            <div>
              <span className={disabledUserClassname(original)}>{first.join(', ')}</span>
              {teamsCount > listShownCount && (
                <Tooltip
                  id={`teams-tooltip-${original.id}`}
                  tooltip={[
                    ...tooltipItems.map(team => <div key={team}>{team}</div>),
                    <div key="more-teams-count">{tooltipMoreCount > 0 && `+ ${tooltipMoreCount} More`}</div>,
                  ]}
                >
                  <span className={disabledUserClassname(original)}>
                    &nbsp; + {teamsCount - first.length} More
                  </span>
                </Tooltip>
              )}
            </div>
          );
        },
        style: {
          whiteSpace: 'pre-line',
          overflow: 'visible',
        },
      }, {
        id: 'actions',
        Header: <TableHeader title="Actions" />,
        accessor: 'actions',
        resizable: false,
        sortable: false,
        width: 100,
        // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now
        Cell: ({ original }: ColumnRenderParameter) => deletingUserId === original.id
          ? <Loader size={30} style={{ height: '30px', width: '30px' }} />
          : <UserEditActions
              userId={original.id}
              canEditUsers={canEditUsers}
              canDeleteUser={canDeleteUser}
              confirmDeleteUser={() => confirmDeleteUser(original.id)}
            />,
      });
    }
    return cols;
  };

  return (
    <SimpleReactTable
      data={filteredUsers()}
      columns={getColumns()}
      defaultSortMode={[sortMode]}
      onSortedChanged={(newSortMode: SortingRule) => setSortMode(newSortMode)}
      showPagination
      cacheId="organizationUsers"
    />
  );
};

const mapStateToProps = createStructuredSelector<ReduxStore, ReduxStateProps>({
  teams: TeamsModule.selectors.getTeamList,
});

const mapDispatchToProps: DispatchProps = {
  deleteUser: UsersModule.actions.delete,
};

export default connect(mapStateToProps, mapDispatchToProps)(UsersTable);
